import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { TTransformedRole } from '@/shared/features/rbac/hooks/use-roles-transformer/use-roles-transformer.types';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { UserRoleFragment } from '@/shared/types/types.generated';
import { getFullname } from '@/shared/utils/format';

export const useRolesTransformer = () => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const { useTenantSettings } = useRbacConfig();
  const { companyName } = useTenantSettings();

  const transformRole = useCallback(
    (role: UserRoleFragment): TTransformedRole => {
      const getRoleType = () => {
        if (role.isSystemRole) {
          return t('rbac:system_role');
        }

        if (!companyName) {
          return '';
        }

        return t('rbac:managed_by', { companyName });
      };

      const roleType = getRoleType();

      const creationDate = formatLocalTime(role.creationTimestamp);

      const userTags = role.users.items.map((user) => ({
        name: getFullname(user),
      }));

      const ssoGroupTags =
        role.ssoGroupMapping?.map((name) => ({ name })) ?? [];

      return {
        userRoleId: role.userRoleId,
        name: role.name,
        isSystemRole: role.isSystemRole,
        roleType,
        creationDate,
        creationTimestamp: role.creationTimestamp,
        userTags,
        ssoGroupTags,
        users: role.users,
        ssoGroupMapping: role.ssoGroupMapping,
        groupId: role.groupId,
        raw: role,
      };
    },
    [t, companyName],
  );

  return { transformRole };
};
